import { Calendar, Thermometer, Cloud, Eye } from 'lucide-react';
import GuideHero from '../../components/guides/GuideHero';
import GuideCTA from '../../components/guides/GuideCTA';
import InsectSection from '../../components/guides/InsectSection';

const insectData = [
  {
    name: 'Mayflies',
    features: 'Upright wings, long tails, delicate body',
    lifeCycle: 'Egg \u2192 Nymph \u2192 Adult',
    whereFound: 'Under rocks in shallow riffles',
    behavior: 'Nymphs crawl from rocks, swim to surface, emerge as adults',
    images: [
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/m5rk9h3p_callibaetis%20mayfly.png', alt: 'Callibaetis Mayfly Adult', caption: 'Callibaetis Mayfly (Adult)' },
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/wrq5tkvw_mayfly%20nymph.png', alt: 'Mayfly Nymph', caption: 'Mayfly Nymph' },
    ],
    species: ['Blue Winged Olives (BWO)', 'Pale Morning Duns (PMD)', 'Hendricksons', 'Green Drakes'],
    fishingTip: 'During emergence, fish both nymphs deep and dry flies on the surface. Look for steady, sipping rises.',
  },
  {
    name: 'Caddisflies',
    features: 'Tent-like wings, long antennae, case builders',
    lifeCycle: 'Egg \u2192 Larva \u2192 Pupa \u2192 Adult',
    whereFound: 'In cases attached to rocks and branches',
    behavior: 'Use air bubbles to swim vertically to surface',
    images: [
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/wowj43se_Caddisfly%20Adult.png', alt: 'Caddisfly Adult', caption: 'Caddisfly Adult' },
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/vn8p72xy_caddisfly%20larva.png', alt: 'Caddisfly Larva', caption: 'Caddisfly Larva (Case Builder)' },
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/kgc0ycyh_Caddisfly%20pupa.png', alt: 'Caddisfly Pupa', caption: 'Caddisfly Pupa' },
    ],
    species: ['Spotted Sedge', 'Little Sister Sedge', 'October Caddis', 'Grannom'],
    fishingTip: 'Look for splashy rises when caddis are active. Adults often skitter across the water surface.',
  },
  {
    name: 'Stoneflies',
    features: 'Flat wings, large size, crawlers',
    lifeCycle: 'Egg \u2192 Nymph \u2192 Adult',
    whereFound: 'Fast, rocky sections with high oxygen',
    behavior: 'Crawl to banks to emerge, don\'t swim to surface',
    images: [
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/5zduy78m_stonefly.png', alt: 'Stonefly Adult', caption: 'Stonefly Adult' },
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/9bodgv2p_stonefly%20larva.png', alt: 'Stonefly Nymph', caption: 'Stonefly Nymph' },
    ],
    species: ['Golden Stone', 'Yellow Sally', 'Little Brown Stone', 'Giant Black Stone'],
    fishingTip: 'Fish stonefly patterns tight to banks and structure. Early spring when water hits 12\u00B0C (55\u00B0F).',
  },
  {
    name: 'Midges',
    features: 'Tiny size, long legs, year-round',
    lifeCycle: 'Egg \u2192 Larva \u2192 Pupa \u2192 Adult',
    whereFound: 'Present in all water types, year-round',
    behavior: 'Cluster in rafts on surface, swarm above water',
    images: [
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/owmf14zc_chironomid.jpg', alt: 'Chironomid Midge Adult', caption: 'Chironomid Midge Adult' },
    ],
    species: ['Chironomids', 'Winter midges', 'Cluster midges', 'Various colors (red, black, olive)'],
    fishingTip: 'Essential for winter fishing across North America. Use tiny patterns (#20-24) and long, fine leaders.',
  },
  {
    name: 'Terrestrials',
    features: 'Land insects, summer/fall, opportunistic',
    lifeCycle: 'Live on land, fall or blown into water',
    whereFound: 'Grassy banks, overhanging vegetation',
    behavior: 'Wind, rain, or accidents bring them to water',
    images: [
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/mnvg1lm9_common%20greeen%20grasshopper.png', alt: 'Common Green Grasshopper', caption: 'Common Green Grasshopper' },
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/trqb0zz4_flying%20ant.jpg', alt: 'Flying Ant', caption: 'Flying Ant' },
      { src: 'https://customer-assets.emergentagent.com/job_angler-connect/artifacts/6iqv2a0x_black%20beetle.jpg', alt: 'Black Beetle', caption: 'Black Beetle' },
    ],
    species: ['Grasshoppers', 'Ants', 'Beetles', 'Inchworms'],
    fishingTip: 'Best during late summer on windy days. Fish close to grassy banks and overhanging trees.',
  },
];

const observationSteps = [
  { num: 1, title: 'Observe the Banks', text: "Walk along the stream and shake bushes and vegetation. Note what insects fall out - their size, shape, and color. This tells you what's available to fall into the water." },
  { num: 2, title: 'Check the Water Surface', text: "Look for insects floating on the water. Are there mayflies with upright wings? Caddis with tent-like wings? This indicates active hatches." },
  {
    num: 3, title: 'Watch for Rising Fish', text: 'Different rise forms tell different stories:',
    details: [
      { label: 'Gentle sips:', val: 'Mayflies or small midges' },
      { label: 'Splashy rises:', val: 'Caddisflies' },
      { label: 'Head-and-tail rises:', val: 'Large insects like drakes' },
      { label: 'No rises:', val: 'Fish are feeding subsurface' },
    ],
  },
  {
    num: 4, title: 'Make Your Choice', text: 'Based on your observations:',
    details: [
      { label: 'Rising fish + insects on water:', val: 'Dry flies' },
      { label: 'No rises but insects present:', val: 'Emergers or nymphs' },
      { label: 'No activity:', val: 'Searching patterns or streamers' },
      { label: 'Windy day with terrestrials:', val: 'Ant, hopper, or beetle patterns' },
    ],
  },
];

const proTips = [
  { Icon: Calendar, title: 'Time of Day:', text: 'Most hatches occur during specific times. Midges in winter, mayflies in evening, caddis at dusk.' },
  { Icon: Thermometer, title: 'Water Temperature:', text: 'Most insects become active when water reaches 10-15\u00B0C (50-59\u00B0F).' },
  { Icon: Cloud, title: 'Weather:', text: 'Overcast days often produce better hatches. Barometric pressure changes trigger activity.' },
  { Icon: Eye, title: 'Season:', text: 'Spring brings stoneflies, summer has caddis and mayflies, fall features terrestrials.' },
];

const AquaticInsects = () => (
  <div className="min-h-screen bg-[#0F1419]">
    <GuideHero
      title="Understanding North American Aquatic Insects"
      description="Success in fly fishing comes from understanding what fish eat. Here are the five main categories of aquatic food that trout encounter across North America:"
    />

    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {insectData.map((insect) => (
          <InsectSection key={insect.name} {...insect} />
        ))}
      </div>
    </section>

    <section className="py-16 bg-[#151b21]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">How to Determine What Fish Are Eating</h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            The key to successful fly fishing is observation. Here's a systematic approach to reading the water and choosing the right fly:
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {observationSteps.map((step) => (
            <div key={step.num} className="bg-[#1E2328] rounded-2xl p-8 shadow-lg">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-[#137dc5]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#3aa0e0]">{step.num}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 mb-3">{step.text}</p>
                  {step.details && (
                    <ul className="space-y-2 text-sm text-slate-400">
                      {step.details.map((d) => (
                        <li key={d.label} className="flex items-start gap-2">
                          <span className="text-[#3aa0e0] font-bold mt-0.5">&bull;</span>
                          <span><strong>{d.label}</strong> {d.val}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-3xl p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Pro Observation Tips</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {proTips.map(({ Icon, title, text }) => (
              <div key={title} className="flex gap-4">
                <Icon className="w-8 h-8 flex-shrink-0" />
                <div>
                  <h4 className="font-bold mb-2 text-lg">{title}</h4>
                  <p className="text-cyan-50">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <GuideCTA
      title="Master North American Waters"
      description="Understanding what fish eat is the foundation of fly fishing success. The FlyFinder app combines this insect knowledge with real-time data about your specific location - water temperature, weather conditions, and seasonal activity - to recommend exactly what insects are active and which flies to use."
    />
  </div>
);

export default AquaticInsects;
